// Auction QR print source adapter for single-item and bulk auction signs.
// Preserves Auction entitlement and public-item destination URLs; excludes
// placeholder records and supplies search/status/publication filters. All print
// layout, size, content, sponsor, pagination, and browser logic is QrPrintView's.
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useQrPrintAssets } from "../../hooks/use_qr_print_assets";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import { AUCTION_STATUS, getAuctionStatus, getAuctionStatusLabel } from "../../utils/auction_status";
import { logger } from "../../utils/logger";
import FullPageSpinner from "../../components/FullPageSpinner";
import AddonRequired from "../../components/AddonRequired";
import QrPrintView from "../../components/QrPrintView";

export default function AuctionQrPrint() {
  const { itemId } = useParams();
  const { eventId, eventslug, eventDoc, eventLoading, ready } = useEventContext({ redirectIfNotFound: true });
  const enabled = hasPurchasedAddOn(eventDoc, "auction");
  const assets = useQrPrintAssets(ready && enabled ? eventId : null);
  const [data, setData] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPublication, setFilterPublication] = useState("");
  const sourceKey = `${eventId}:${itemId || "all"}`;

  useEffect(() => {
    if (!ready || !eventId || !enabled) return;
    let active = true;
    setData(null);
    const load = async () => {
      try {
        let records;
        if (itemId) {
          const snapshot = await getDoc(doc(db, "events", eventId, "auctionItems", itemId));
          records = snapshot.exists() ? [{ ...snapshot.data(), id: snapshot.id }] : [];
        } else {
          const snapshot = await getDocs(collection(db, "events", eventId, "auctionItems"));
          records = snapshot.docs.map((record) => ({ ...record.data(), id: record.id }));
        }
        records = records.filter((record) => record.placeholder !== true)
          .sort((left, right) => String(left.title || "").localeCompare(String(right.title || "")));
        if (active) setData({ key: sourceKey, records, error: "" });
      } catch (error) {
        logger.error("Failed to load auction QR items", { value: error });
        if (active) setData({ key: sourceKey, records: [], error: "Error loading auction QR items." });
      }
    };
    load();
    return () => { active = false; };
  }, [ready, eventId, itemId, enabled, sourceKey]);

  const cards = useMemo(() => (data?.records || []).map((item) => ({
    id: item.id,
    item,
    // QrCard uses entry-like fields; donor details never include bidder identity.
    car: {
      id: item.id, catid: "auction", ownerFirst: item.donorName || "", ownerLast: "",
      description: `${item.title || item.linkedEntryLabel || "Auction Item"} — ${item.description ||
        `Starting Bid: $${(Number(item.startingBidCents ?? Math.round(Number(item.startingBid || 0) * 100)) / 100).toFixed(2)}`}`,
    },
    url: `${window.location.origin}/${eventslug}/auction/${item.auctionSlug || item.id}`,
  })), [data, eventslug]);
  const filteredCards = useMemo(() => itemId ? cards : cards.filter(({ item }) => {
    const matchesText = [item.title, item.linkedEntryLabel, item.description].filter(Boolean).join(" ").toLowerCase().includes(filterText.trim().toLowerCase());
    const matchesStatus = !filterStatus || getAuctionStatus({ item, event: eventDoc }) === filterStatus;
    const matchesPublication = !filterPublication || (item.published === true) === (filterPublication === "published");
    return matchesText && matchesStatus && matchesPublication;
  }), [cards, itemId, filterText, filterStatus, filterPublication, eventDoc]);

  if (!ready || eventLoading) return <FullPageSpinner message="Loading Auction QR Codes..." />;
  if (!enabled) return <AddonRequired eventslug={eventslug} title="Silent Auction Add-on Required"
    description="This event has not purchased the Silent Auction add-on yet." />;
  if (assets.loading || data?.key !== sourceKey) return <FullPageSpinner message="Loading Auction QR Codes..." />;
  if (data.error || assets.error) return <div className="container mt-5 alert alert-danger" role="alert">{data.error || assets.error}</div>;
  if (itemId && !cards.length) return <div className="container mt-5 alert alert-info">Auction item not found.</div>;

  return <QrPrintView key={sourceKey} cards={cards} filteredCards={filteredCards} single={!!itemId}
    title={itemId ? "🏷️ Print Auction QR" : "🏷️ Auction QR Print View"}
    description={itemId ? "Scan to open this Silent Auction item." : "Print QR codes for Silent Auction items."}
    assets={assets} eventName={eventDoc?.name} categoriesMap={{ auction: "Silent Auction" }}
    onResetFilters={() => { setFilterText(""); setFilterStatus(""); setFilterPublication(""); }}
    filters={<div className="col-md-6 mb-2"><div className="row g-2">
      <div className="col-12">
        <label className="form-label" htmlFor="qr-auction-search">Search Auction Items</label>
        <input id="qr-auction-search" className="form-control" placeholder="Item title or description" value={filterText} onChange={(event) => setFilterText(event.target.value)} />
      </div>
      <div className="col-sm-6">
        <label className="form-label" htmlFor="qr-auction-status">Filter by Status</label>
        <select id="qr-auction-status" className="form-select" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
          <option value="">All</option>
          {Object.values(AUCTION_STATUS).map((status) => <option key={status} value={status}>{getAuctionStatusLabel(status)}</option>)}
        </select>
      </div>
      <div className="col-sm-6">
        <label className="form-label" htmlFor="qr-auction-publication">Publication</label>
        <select id="qr-auction-publication" className="form-select" value={filterPublication} onChange={(event) => setFilterPublication(event.target.value)}>
          <option value="">All</option><option value="published">Published</option><option value="unpublished">Unpublished</option>
        </select>
      </div>
    </div></div>}
  />;
}
